import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { GlossaryTerm } from "@/types/glossary";
import {
  compileGlossaryRegex,
  createGlossaryClaims,
  type CompiledGlossary,
  type GlossaryClaims,
} from "@/lib/glossaryHighlight";

interface GlossaryHighlightValue {
  enabled: boolean;
  compiled: CompiledGlossary | null;
  claims: GlossaryClaims;
}

const GlossaryHighlightContext = createContext<GlossaryHighlightValue | null>(
  null
);

export const useGlossaryHighlight = (): GlossaryHighlightValue | null =>
  useContext(GlossaryHighlightContext);

/**
 * Enables glossary auto-linking for CourseMarkdown descendants: it exposes the
 * compiled term matcher, and its presence gates highlighting so markdown
 * rendered outside a subchapter (intro pages, the glossary slideover) stays
 * untouched. The claims registry scopes "first occurrence" to the whole
 * subchapter across all markdown blocks; pass the subchapter id as `resetKey`
 * so navigating to another subchapter starts with fresh claims.
 */
export const GlossaryHighlightProvider = ({
  terms,
  resetKey,
  children,
}: {
  terms: GlossaryTerm[];
  resetKey?: string | null;
  children: ReactNode;
}) => {
  const compiled = useMemo(
    () => (terms.length > 0 ? compileGlossaryRegex(terms) : null),
    [terms]
  );

  // A new registry whenever the subchapter or the term set changes — the deps
  // are the cache key, not inputs of the factory.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const claims = useMemo(() => createGlossaryClaims(), [resetKey, compiled]);

  const value = useMemo<GlossaryHighlightValue>(
    () => ({ enabled: compiled !== null, compiled, claims }),
    [compiled, claims]
  );

  return (
    <GlossaryHighlightContext.Provider value={value}>
      {children}
    </GlossaryHighlightContext.Provider>
  );
};
